import _isEqual from 'lodash.isequal';
import _omit from 'lodash.omit';

import CONNECTION_STATES from 'lib/common/constants/connectionStates';
import CONTACT_STATES from 'lib/common/constants/contactStates';

import TTask from 'lib/common/types/Task';
import { isConference as isConferenceContact } from 'lib/common/utils/conferenceConnections';
import connectGetter from 'lib/common/utils/connectGetter';

import THandleContactChange from '../types/HandleContactChange';
import buildConnectionTimestamps from './buildConnectionTimestamps';

export default function handleCallRefresh({
  handleContactChange,
  contact,
  existingTask,
  moveTaskToACW,
}: {
  handleContactChange: THandleContactChange;
  contact: connect.Contact;
  existingTask: TTask;
  moveTaskToACW: (contact: connect.Contact) => void;
}) {
  const contactType = connectGetter(contact, 'getType');
  const contactState = connectGetter(contact, 'getState')?.type;

  const isConference = isConferenceContact(contact);
  const isCall = contactType === connect.ContactType.VOICE || contactType === connect.ContactType.QUEUE_CALLBACK;
  const isConnectedCall = contactState === connect.ContactStateType.CONNECTED && isCall;

  // If task is not conference or connected, we bail out
  if (!isConference && !isConnectedCall) {
    return;
  }

  // Manually move to ACW if the conference call has ended
  if (isConference && connectGetter(contact, 'getState')?.type === connect.ContactStateType.ENDED) {
    return moveTaskToACW(contact);
  }

  // Building connection timestamps for conference to keep them in order
  // because we only show (agent) as connection name for internal transfers
  const connectionTimestamps = buildConnectionTimestamps({ contact: contact, task: existingTask });

  const initialConnection = connectGetter(contact, 'getInitialConnection');
  const thirdPartyConnection = connectGetter(contact, 'getSingleActiveThirdPartyConnection');
  const relevantConnection = connectGetter(initialConnection, 'isActive') ? initialConnection : thirdPartyConnection;
  const relevantConnectionState = connectGetter(relevantConnection, 'isOnHold')
    ? CONNECTION_STATES.HOLD
    : CONNECTION_STATES.LIVE;

  // This logic is to check if contact connections have changed rather than just connection state timestamp changes to reduce unnecessary state update.
  const latestContactConnections = contact
    .toSnapshot()
    // @ts-expect-error - contact snapshot is not typed correctly by connect
    .contactData?.connections.map((connection) => ({
      ...connection,
      state: _omit(connection.state, 'timestamp'),
    }));
  // @ts-expect-error - contact snapshot is not typed correctly by connect
  const voiceTaskConnections = existingTask?.contactSnapshot?.contactData?.connections.map((connection) => ({
    ...connection,
    state: _omit(connection.state, 'timestamp'),
  }));

  // If no change in connections, do not update tasks state
  if (_isEqual(voiceTaskConnections, latestContactConnections)) {
    return;
  }

  handleContactChange({
    contact,
    status: CONTACT_STATES.CONNECTED,
    connectionState: relevantConnectionState,
    connectionTimestamps,
  });
}
